import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Check, Search, Minus } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import { Container, Reveal } from "@/components/Reveal";
import USMap, { MapLegend } from "@/components/USMap";
import { ServiceBadge } from "@/components/ServiceBadge";
import ContactForm from "@/components/ContactForm";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  listStates,
  getState,
  getServices,
  tierColor,
  availableServices,
  requirementText,
  FEATURED_SLUGS,
  SUPPORTED_SLUGS,
  LANDING_FAQS,
  SERVICE_META,
} from "@/data/serviceAreas";

const FILTERS = [
  { key: "all", label: "All Services" },
  { key: "electricity", label: "Commercial Electricity" },
  { key: "gas", label: "Commercial Natural Gas" },
  { key: "battery", label: "Virtual Power Plant Network" },
];

function StateCard({ st, testid }) {
  const svc = getServices(st.slug);
  const note = st.gas?.note || st.electricity?.note;
  return (
    <Link
      to={`/service-areas/${st.slug}`}
      data-testid={testid}
      className="group flex h-full flex-col border border-line bg-white p-6 transition-colors hover:border-electric"
    >
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-heading text-xl font-medium text-navy">{st.name}</h3>
          <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.14em] text-slatey">
            {st.abbr}
            {st.grid ? ` · ${st.grid}` : ""}
          </p>
        </div>
        <span
          className="mt-1.5 h-3 w-3 shrink-0 rounded-sm"
          style={{ backgroundColor: tierColor(st.slug) }}
        />
      </div>
      <div className="mt-5 flex flex-wrap gap-2">
        {svc.electricity && <ServiceBadge serviceKey="electricity" size="sm" />}
        {svc.gas && <ServiceBadge serviceKey="gas" size="sm" />}
        {svc.battery && <ServiceBadge serviceKey="battery" size="sm" />}
      </div>
      {note && (
        <p className="mt-4 font-mono text-[10px] leading-relaxed text-slatey">{note}</p>
      )}
      <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-electric">
        View state
        <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
      </span>
    </Link>
  );
}

export default function ServiceAreas() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");

  const states = useMemo(() => listStates(), []);
  const filtered = useMemo(() => {
    if (filter === "all") return states;
    return states.filter((s) => !!s[filter]);
  }, [states, filter]);

  const searched = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return states;
    return states.filter(
      (s) => s.name.toLowerCase().includes(q) || s.abbr.toLowerCase().includes(q)
    );
  }, [states, query]);

  const featured = FEATURED_SLUGS.map((slug) => getState(slug));

  return (
    <>
      <SEO
        title="Service Areas"
        description="A state-by-state commercial energy directory. Explore where Innervation Energy Group offers commercial electricity, natural gas, and Virtual Power Plant Network hosting across deregulated U.S. markets."
        path="/service-areas"
      />
      <PageHero
        overline="Service Areas"
        titleLines={["A commercial energy", "atlas of the U.S."]}
        subtitle="Energy markets are regulated state by state — so where and how businesses can buy energy varies across the country. Explore our coverage below and see what's available in your market."
        crumbs={[{ label: "Service Areas", path: "/service-areas" }]}
      />

      {/* Interactive map */}
      <section className="relative overflow-hidden bg-navy-dark py-20 text-white md:py-24">
        <Container>
          <div className="grid grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-7">
              <div className="rounded-sm border border-white/10 bg-navy/40 p-4 md:p-8">
                <USMap className="relative" onSelect={(id) => navigate(`/service-areas/${id}`)} />
              </div>
            </div>
            <div className="lg:col-span-5">
              <p className="overline text-energy">Interactive Coverage Map</p>
              <h2 className="mt-4 font-heading text-2xl font-semibold tracking-tight md:text-3xl">
                Hover a market. Click to explore.
              </h2>
              <p className="mt-5 leading-relaxed text-white/65">
                Supported markets are highlighted and clickable; hover any state to see
                the services available there. Virtual Power Plant Network hosting is
                currently evaluated in Texas and Illinois.
              </p>
              <div className="mt-8">
                <MapLegend dark />
              </div>
              <div className="mt-10 grid grid-cols-3 gap-4 border-t border-white/10 pt-8">
                <div>
                  <div className="font-heading text-3xl font-semibold">{SUPPORTED_SLUGS.length}</div>
                  <p className="mt-1 text-xs text-white/50">Supported markets</p>
                </div>
                <div>
                  <div className="font-heading text-3xl font-semibold">3</div>
                  <p className="mt-1 text-xs text-white/50">Core services</p>
                </div>
                <div>
                  <div className="font-heading text-3xl font-semibold">2</div>
                  <p className="mt-1 text-xs text-white/50">Battery markets</p>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Featured states */}
      <section className="bg-white py-24 md:py-28">
        <Container>
          <p className="overline text-electric">Featured Markets</p>
          <h2 className="mt-4 font-heading text-3xl font-semibold tracking-tight text-navy md:text-4xl">
            Our most active commercial energy markets.
          </h2>
          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {featured.map((st, i) => (
              <Reveal key={st.slug} delay={(i % 3) * 0.07}>
                <Link
                  to={`/service-areas/${st.slug}`}
                  data-testid={`featured-state-${st.slug}`}
                  className="group flex h-full flex-col overflow-hidden border border-line transition-colors hover:border-electric"
                >
                  <div
                    className="relative flex h-28 items-end p-6"
                    style={{ backgroundColor: tierColor(st.slug) }}
                  >
                    <span className="font-heading text-4xl font-semibold text-white/90">
                      {st.abbr}
                    </span>
                    <span className="absolute right-6 top-6 font-mono text-[11px] uppercase tracking-[0.14em] text-white/70">
                      {st.grid || "Regulated grid"}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <h3 className="font-heading text-2xl font-medium text-navy">{st.name}</h3>
                    <p className="mt-3 flex-1 leading-relaxed text-slatey">{st.overview}</p>
                    <div className="mt-5 flex flex-wrap gap-2">
                      {availableServices(st).map((s) => (
                        <ServiceBadge key={s.key} serviceKey={s.key} size="sm" />
                      ))}
                    </div>
                    <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-electric">
                      View {st.name}
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* Browse by service + state */}
      <section className="bg-ice py-24 md:py-28">
        <Container>
          <p className="overline text-electric">Browse Coverage</p>
          <h2 className="mt-4 font-heading text-3xl font-semibold tracking-tight text-navy md:text-4xl">
            Browse by service and state.
          </h2>

          <div className="mt-10 flex flex-wrap gap-3" data-testid="service-filters">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`filter-${f.key}`}
                className={
                  "border px-5 py-2.5 font-mono text-xs uppercase tracking-[0.12em] transition-colors " +
                  (filter === f.key
                    ? "border-navy bg-navy text-white"
                    : "border-line bg-white text-slatey hover:border-navy hover:text-navy")
                }
              >
                {f.label}
              </button>
            ))}
          </div>

          <p className="mt-6 text-sm text-slatey" data-testid="filter-count">
            Showing <span className="font-medium text-navy">{filtered.length}</span>{" "}
            {filtered.length === 1 ? "market" : "markets"}
            {filter !== "all" && ` with ${FILTERS.find((f) => f.key === filter).label}`}.
          </p>

          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((st, i) => (
              <Reveal key={st.slug} delay={(i % 3) * 0.05}>
                <StateCard st={st} testid={`browse-state-${st.slug}`} />
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* Service comparison */}
      <section className="bg-white py-24 md:py-28">
        <Container>
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="overline text-electric">Service Comparison</p>
              <h2 className="mt-4 font-heading text-3xl font-semibold tracking-tight text-navy md:text-4xl">
                Compare availability at a glance.
              </h2>
            </div>
            <div className="relative w-full max-w-xs">
              <Search className="pointer-events-none absolute left-0 top-1/2 h-4 w-4 -translate-y-1/2 text-slatey" />
              <input
                data-testid="comparison-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search a state…"
                className="w-full border-0 border-b border-line bg-transparent py-3 pl-6 pr-0 text-navy outline-none transition-colors placeholder:text-slatey/60 focus:border-electric"
              />
            </div>
          </div>

          <div className="mt-10 overflow-x-auto border border-line">
            <table className="w-full min-w-[760px] border-collapse text-left" data-testid="comparison-table">
              <thead>
                <tr className="border-b border-line bg-ice">
                  {["State", "Electricity", "Natural Gas", "VPP Network", "Special Requirements"].map((h) => (
                    <th
                      key={h}
                      className="px-5 py-4 font-mono text-[11px] uppercase tracking-[0.12em] text-slatey"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {searched.map((st) => {
                  const svc = getServices(st.slug);
                  const req = requirementText(st);
                  return (
                    <tr
                      key={st.slug}
                      data-testid={`comparison-row-${st.slug}`}
                      className="border-b border-line transition-colors last:border-b-0 hover:bg-ice/60"
                    >
                      <td className="px-5 py-4">
                        <Link
                          to={`/service-areas/${st.slug}`}
                          className="font-medium text-navy hover:text-electric"
                        >
                          {st.name}
                        </Link>
                      </td>
                      {["electricity", "gas", "battery"].map((k) => (
                        <td key={k} className="px-5 py-4">
                          {svc[k] ? (
                            <span
                              className="inline-flex items-center gap-1.5 text-sm font-medium"
                              style={{ color: SERVICE_META[k].color }}
                            >
                              <Check className="h-4 w-4" /> Available
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 text-sm text-slatey/50">
                              <Minus className="h-4 w-4" />
                            </span>
                          )}
                        </td>
                      ))}
                      <td className="px-5 py-4 text-sm text-slatey">{req}</td>
                    </tr>
                  );
                })}
                {searched.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-5 py-10 text-center text-slatey">
                      No markets match “{query}”. Try another state.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Container>
      </section>

      {/* FAQ */}
      <section className="bg-ice py-24 md:py-28">
        <Container>
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-4">
              <p className="overline text-electric">FAQ</p>
              <h2 className="mt-4 font-heading text-3xl font-semibold leading-[1.12] tracking-tight text-navy md:text-4xl">
                How state energy markets work.
              </h2>
              <p className="mt-5 leading-relaxed text-slatey">
                Common questions about deregulation, availability, and how coverage
                varies across the country.
              </p>
            </div>
            <div className="lg:col-span-7 lg:col-start-6">
              <Accordion type="single" collapsible className="border-t border-line">
                {LANDING_FAQS.map((f, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="border-line">
                    <AccordionTrigger className="py-5 text-left text-base text-navy hover:no-underline">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-slatey">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </Container>
      </section>

      {/* Contact */}
      <section className="bg-white py-24 md:py-28">
        <Container>
          <div className="grid grid-cols-1 gap-14 lg:grid-cols-2 lg:gap-20">
            <div>
              <p className="overline text-electric">Contact</p>
              <h2 className="mt-4 font-heading text-3xl font-semibold leading-[1.12] tracking-tight text-navy md:text-4xl">
                Operating in one of these markets?
              </h2>
              <p className="mt-6 max-w-lg text-lg leading-relaxed text-slatey">
                Tell us where your facilities are located and what you'd like help with.
                A commercial energy advisor will confirm availability and next steps.
              </p>
            </div>
            <Reveal delay={0.1}>
              <div className="border border-line bg-ice p-8 md:p-10">
                <ContactForm />
              </div>
            </Reveal>
          </div>
        </Container>
      </section>
    </>
  );
}
